import qs from 'qs';
import { types as waiverActionTypes } from '../waivers/waiversActions';
import {
  WaiverWorkItem,
  WaiverWorkItemAction,
  Resolution,
} from '../../../../types/waiverWorkItem';
import { Dispatch } from '../../../types';
import asyncAction from '../../../asyncActionCreator';
import { updateWaiverWorkItem } from './WaiverWorkItemDetailsActions';
import {
  startChangeWaiverAssociation,
  endChangeWaiverAssociation,
} from '../../../components/waivers/workItem/waiverToChangeAssociationActions';
import { updateChangeEventId as serviceUpdateWaiverChangeEventId } from '../../../../service/waiverService';
import { Waiver } from '../../../../types/waiver';

export const editNewWaiver: WaiverWorkItemAction = {
  name: 'NEW_WAIVER',
  description: 'Create New Waiver',
  executeSuccessMessage:
    'New waivers associated with this work item may take a few moments to appear. You must refresh the page to see these changes.',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    openWaiverEditor(workItem, 'new'),
};

export function createEditExistingWaiverAction(
  waiver: Waiver,
): WaiverWorkItemAction {
  return {
    name: `UPDATE_WAIVER_${waiver.id}`,
    description: `Update Waiver: ${waiver.name}`,
    executeSuccessMessage:
      'Waivers updates associated with this work item may take a few moments to appear. You must refresh the page to see these changes.',
    action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
      openWaiverEditor(workItem, waiver.id as number),
  };
}

export function createChangeWaiverAssociationAction(
  waiver: Waiver,
): WaiverWorkItemAction {
  return {
    name: `UPDATE_ASSOCIATON_${waiver.id}`,
    description: `Update Association For Waiver: ${waiver.name}`,
    action: (dispatch: Dispatch, _workItem: WaiverWorkItem) =>
      new Promise((resolve, _reject) =>
        resolve(dispatch(startChangeWaiverAssociation(waiver))),
      ),
  };
}

export function completeChangeWaiverAssociationAction(): WaiverWorkItemAction {
  return {
    name: 'COMPLETE_WAIVER_REASSOCIATON',
    description: 'Completed Association For Waiver',
    action: (dispatch: Dispatch, _workItem: WaiverWorkItem) =>
      new Promise((resolve, _reject) =>
        resolve(dispatch(endChangeWaiverAssociation())),
      ),
    executeSuccessMessage:
      'Any changes in waiver associations may take a few moments to appear. You must refresh the page to see these changes.',
    executeErrorMessage: 'A problem occurred changing the waiver association.',
  };
}

export function associateWithWaiver(waiver: Waiver): WaiverWorkItemAction {
  return {
    name: `ASSOCIATE_WITH_WAIVER_${waiver.id}`,
    description: `Associate Waiver: ${waiver.name}`,
    action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
      associateWithWaiverAndResolve(dispatch, workItem, waiver, 'WAIVER'),
    executeSuccessMessage: `Work item has been associated with ${waiver.name}`,
    executeErrorMessage: `Work item has not been associated with ${waiver.name}`,
  };
}

export const editAirlineConfiguration: WaiverWorkItemAction = {
  name: 'UPDATE_AIRLINE_CONFIGURATION',
  description: 'Update Airline Configuration',
  action: (_dispatch: Dispatch, _workItem: WaiverWorkItem) =>
    openInNewTab('/waivers/configurations'),
};

export const resolveWorkItemAsWaiver: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_WAIVER',
  description: 'Close',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'WAIVER')),
  executeSuccessMessage: 'Work item has been closed',
  executeErrorMessage: 'Work item resolution failed',
};

export const resolveWorkItemAsNotWaiver: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_NOT_WAIVER',
  description: 'Not a waiver',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'NOT_WAIVER')),
  executeSuccessMessage: 'Work item has been marked as not a waiver',
  executeErrorMessage: 'Work item resolution failed',
};

export const resolveWorkItemAsFalsePositive: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_FALSE_POSITIVE',
  description: 'False positive',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'FALSE_POSITIVE')),
  executeSuccessMessage: 'Work item has been marked as a false positive',
  executeErrorMessage: 'Work item resolution failed',
};

export const resolveWorkItemAsNotQualifyingWaiver: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_NOT_QUALIFYING_WAIVER',
  description: 'Not qualifying waiver',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'NOT_QUALIFYING_WAIVER')),
  executeSuccessMessage: 'Work item has been marked as a not qualifying waiver',
  executeErrorMessage: 'Work item resolution failed',
};

export const resolveWorkItemAsSuperseded: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_SUPERSEDED',
  description: 'Superseded by a newer item',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'SUPERSEDED')),
  executeSuccessMessage:
    'Work item has been marked as superseded by another item',
  executeErrorMessage: 'Work item resolution failed',
};

export const resolveWorkItemAsWaiverMissing: WaiverWorkItemAction = {
  name: 'RESOLVE_AS_WAIVER_MISSING',
  description: 'Waiver Missing',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(resolveWorkItem(workItem, 'WAIVER_MISSING')),
  executeSuccessMessage:
    'Work item has been marked as waiver missing and admins have been notified',
  executeErrorMessage: 'Work item resolution failed',
};

export const reopenWorkItem: WaiverWorkItemAction = {
  name: 'REOPEN',
  description: 'Reopen',
  action: (dispatch: Dispatch, workItem: WaiverWorkItem) =>
    dispatch(reopen(workItem)),
  executeSuccessMessage: 'Work item has been reopened',
  executeErrorMessage: 'Work item reopen has failed',
};

function openWaiverEditor(
  workItem: WaiverWorkItem,
  waiverId: number | 'new',
): Promise<void> {
  const airlineCode = workItem.airlineCode;
  const changeEventId = workItem.changeEventId;
  const issueDate = workItem.createdAt.format('YYYY-MM-DD');
  const queryParams = qs.stringify({ airlineCode, changeEventId, issueDate });
  return openInNewTab(`/waivers/${waiverId}/edit?${queryParams}`) as Promise<void>;
}

function openInNewTab(url: string) {
  return new Promise((resolve, _reject) => {
    resolve(window.open(url, '_blank')?.focus());
  });
}

function resolveWorkItem(workItem: WaiverWorkItem, resolution: Resolution) {
  return updateWaiverWorkItem({
    ...workItem,
    state: 'CLOSED',
    resolution,
  });
}

function reopen(workItem: WaiverWorkItem) {
  return updateWaiverWorkItem({
    ...workItem,
    state: 'OPEN',
    resolution: undefined,
  });
}

function associateWithWaiverAndResolve(
  dispatch: Dispatch,
  workItem: WaiverWorkItem,
  waiver: Waiver,
  resolution: Resolution,
) {
  const action = asyncAction(
    waiverActionTypes.UPDATE_CHANGE_EVENT_ID,
    () => serviceUpdateWaiverChangeEventId(waiver, workItem.changeEventId),
    {
      then: (_dispatch: Dispatch) => resolveWorkItem(workItem, resolution),
    },
  );
  return dispatch(action);
}
